import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import type { ReactNode } from "react";

import { AuthProvider, useAuth } from "./lib/auth";
import { ApiError } from "./lib/api";
import { AppShell } from "./layouts/AppShell";

import { SignIn } from "./pages/auth/SignIn";
import { Register } from "./pages/auth/Register";

import { Overview } from "./pages/merchant/Overview";
import { Payments } from "./pages/merchant/Payments";
import { PaymentDetail } from "./pages/merchant/PaymentDetail";
import { ApiKeys } from "./pages/merchant/ApiKeys";
import { Verification } from "./pages/merchant/Verification";
import { Webhooks } from "./pages/merchant/Webhooks";
import { Payouts } from "./pages/merchant/Payouts";
import { Customers } from "./pages/merchant/Customers";
import { PaymentLinks } from "./pages/merchant/PaymentLinks";
import { Invoices } from "./pages/merchant/Invoices";
import { Subscriptions } from "./pages/merchant/Subscriptions";
import { FraudControls } from "./pages/merchant/FraudControls";
import { Pricing } from "./pages/merchant/Pricing";
import { Team } from "./pages/merchant/Team";

import { AdminOverview } from "./pages/admin/AdminOverview";
import { KycQueue } from "./pages/admin/KycQueue";
import { KycReview } from "./pages/admin/KycReview";
import { Tenants, TenantDetail } from "./pages/admin/Tenants";
import { AdminPayouts } from "./pages/admin/AdminPayouts";
import { Plans } from "./pages/admin/Plans";
import { Ledger } from "./pages/admin/Ledger";
import { Audit } from "./pages/admin/Audit";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      refetchOnWindowFocus: true,
      // Authentication and authorisation failures are never worth retrying: the
      // answer will not change, and retrying only delays showing the user why.
      retry: (failureCount, error) => {
        if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false;
        return failureCount < 2;
      },
    },
  },
});

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <Router />
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

function Router() {
  const { user, ready, isOperator } = useAuth();

  // Nothing is rendered until the stored session has been verified, so a signed-in
  // user never sees the sign-in screen flash before their dashboard.
  if (!ready) return <Booting />;

  return (
    <Routes>
      <Route
        path="/signin"
        element={user ? <Navigate to={isOperator ? "/admin" : "/"} replace /> : <SignIn />}
      />
      <Route
        path="/register"
        element={user ? <Navigate to={isOperator ? "/admin" : "/"} replace /> : <Register />}
      />

      <Route
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        {/* Merchant */}
        <Route index element={isOperator ? <Navigate to="/admin" replace /> : <Overview />} />
        <Route path="payments" element={<Payments />} />
        <Route path="payments/:id" element={<PaymentDetail />} />
        <Route path="payouts" element={<Payouts />} />
        <Route path="customers" element={<Customers />} />
        <Route path="links" element={<PaymentLinks />} />
        <Route path="invoices" element={<Invoices />} />
        <Route path="subscriptions" element={<Subscriptions />} />
        <Route path="fraud" element={<FraudControls />} />
        <Route path="keys" element={<ApiKeys />} />
        <Route path="webhooks" element={<Webhooks />} />
        <Route path="verification" element={<Verification />} />
        <Route path="pricing" element={<Pricing />} />
        <Route path="team" element={<Team />} />

        {/* Operations console */}
        <Route
          path="admin"
          element={
            <RequireOperator>
              <AdminOverview />
            </RequireOperator>
          }
        />
        <Route
          path="admin/verification"
          element={
            <RequireOperator>
              <KycQueue />
            </RequireOperator>
          }
        />
        <Route
          path="admin/verification/:id"
          element={
            <RequireOperator>
              <KycReview />
            </RequireOperator>
          }
        />
        <Route
          path="admin/tenants"
          element={
            <RequireOperator>
              <Tenants />
            </RequireOperator>
          }
        />
        <Route
          path="admin/tenants/:id"
          element={
            <RequireOperator>
              <TenantDetail />
            </RequireOperator>
          }
        />
        <Route
          path="admin/payouts"
          element={
            <RequireOperator>
              <AdminPayouts />
            </RequireOperator>
          }
        />
        <Route
          path="admin/pricing"
          element={
            <RequireOperator>
              <Plans />
            </RequireOperator>
          }
        />
        <Route
          path="admin/ledger"
          element={
            <RequireOperator>
              <Ledger />
            </RequireOperator>
          }
        />
        <Route
          path="admin/audit"
          element={
            <RequireOperator>
              <Audit />
            </RequireOperator>
          }
        />
      </Route>

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function RequireAuth({ children }: { children: ReactNode }) {
  const { user, ready } = useAuth();
  if (!ready) return <Booting />;
  if (!user) return <Navigate to="/signin" replace />;
  return <>{children}</>;
}

/** RequireOperator guards the operations console in the browser.
 *
 *  It is a convenience, not a security control: the API enforces the same rule on
 *  every request, so a user who edits their way past this sees nothing but 403s. */
function RequireOperator({ children }: { children: ReactNode }) {
  const { isOperator } = useAuth();
  if (!isOperator) return <Navigate to="/" replace />;
  return <>{children}</>;
}

function Booting() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="text-center">
        <p className="font-display text-[24px] text-ink">PayFlow</p>
        <div className="mx-auto mt-3 h-px w-16 overflow-hidden bg-rule">
          <div className="draw-rule h-full w-full bg-verdigris" />
        </div>
      </div>
    </div>
  );
}

function NotFound() {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center px-6 text-center">
      <p className="font-display text-[64px] leading-none text-rule-strong">404</p>
      <h1 className="mt-3 text-[22px] text-ink-strong">This page does not exist</h1>
      <p className="mt-1.5 text-[13.5px] text-ink-muted">
        The link may be out of date, or the page may have moved.
      </p>
      <a
        href="/"
        className="mt-6 rounded-md border border-rule-strong bg-paper-raised px-4 py-2 text-[13px] font-medium transition-colors hover:bg-paper-sunk"
      >
        Back to the dashboard
      </a>
    </div>
  );
}
